import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    CircularProgress,
    FormControlLabel,
    Link,
    MenuItem,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Link as RouterLink, Navigate, useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../api';
import { isMasterUser, patchStoredUser } from '../../helpers/authSession';

const emptyForm = {
    name: '',
    email: '',
    password: '',
    store_name: '',
    subscription_uuid: '',
    active: true,
};

function UserFormPage({ mode }) {
    const isEdit = mode === 'edit';
    const { id } = useParams();
    const navigate = useNavigate();
    const [isMaster, setIsMaster] = useState(isMasterUser());
    const [checkingAccess, setCheckingAccess] = useState(!isMasterUser());
    const [plans, setPlans] = useState([]);
    const [formData, setFormData] = useState(emptyForm);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [loadingUser, setLoadingUser] = useState(isEdit);

    useEffect(() => {
        let cancelled = false;
        api.get('/auth/me')
            .then((response) => {
                const user = response?.data?.data;
                if (user && typeof user.is_master === 'boolean') {
                    patchStoredUser({ is_master: user.is_master });
                    if (!cancelled) {
                        setIsMaster(user.is_master);
                    }
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setIsMaster(false);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setCheckingAccess(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!isMaster) {
            return undefined;
        }
        let cancelled = false;
        api.get('/subscription-plans')
            .then((response) => {
                const list = response?.data?.data ?? [];
                if (!cancelled) {
                    const plansList = Array.isArray(list) ? list : [];
                    setPlans(plansList);
                    if (!isEdit) {
                        const free = plansList.find((plan) => {
                            const name = String(plan.name || '').toLowerCase();
                            return name === 'free' || name === 'começando' || name === 'comecando';
                        });
                        if (free?.uuid) {
                            setFormData((prev) => (
                                prev.subscription_uuid
                                    ? prev
                                    : { ...prev, subscription_uuid: free.uuid }
                            ));
                        }
                    }
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setPlans([]);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [isMaster, isEdit]);

    useEffect(() => {
        if (!isEdit || !isMaster || !id) {
            return undefined;
        }
        let cancelled = false;
        setLoadingUser(true);
        api.get(`/users/${id}`)
            .then((response) => {
                const user = response?.data?.data;
                if (!cancelled && user) {
                    setFormData({
                        name: user.name || '',
                        email: user.email || '',
                        password: '',
                        store_name: user.store_name || '',
                        subscription_uuid: user.subscription_uuid || '',
                        active: user.active !== false,
                        is_master: Boolean(user.is_master),
                    });
                }
            })
            .catch(async () => {
                await Swal.fire({ icon: 'error', title: 'Usuário não encontrado' });
                navigate('/admin/users');
            })
            .finally(() => {
                if (!cancelled) {
                    setLoadingUser(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [isEdit, isMaster, id, navigate]);

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: '' }));
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);
        setErrors({});
        try {
            if (!formData.name.trim()) {
                setErrors({ name: 'Informe o nome.' });
                return;
            }
            if (!formData.email.trim()) {
                setErrors({ email: 'Informe o e-mail.' });
                return;
            }
            if (!isEdit && formData.password.length < 8) {
                setErrors({ password: 'A senha deve ter pelo menos 8 caracteres.' });
                return;
            }
            if (isEdit && formData.password && formData.password.length < 8) {
                setErrors({ password: 'A senha deve ter pelo menos 8 caracteres.' });
                return;
            }

            const payload = {
                name: formData.name.trim(),
                email: formData.email.trim(),
                store_name: formData.store_name.trim() || undefined,
                subscription_uuid: formData.subscription_uuid || undefined,
                active: formData.active,
            };
            if (formData.password) {
                payload.password = formData.password;
            }
            if (!isEdit) {
                payload.password = formData.password;
                // Conta criada pelo master: já ativa, sem confirmação de e-mail.
                payload.subscription_active = true;
            }

            if (isEdit) {
                await api.put(`/users/${id}`, payload);
            } else {
                await api.post('/users', payload);
            }

            await Swal.fire({
                icon: 'success',
                title: isEdit ? 'Usuário atualizado' : 'Usuário cadastrado',
                timer: 1800,
                showConfirmButton: false,
            });
            navigate('/admin/users');
        } catch (error) {
            const message = error?.response?.data?.error || error?.response?.data?.message || 'Tente novamente.';
            if (typeof message === 'string' && message.toLowerCase().includes('email')) {
                setErrors({ email: message });
            } else {
                await Swal.fire({ icon: 'error', title: 'Falha ao salvar', text: message });
            }
        } finally {
            setLoading(false);
        }
    };

    if (checkingAccess || loadingUser) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
            </Box>
        );
    }
    if (!isMaster) {
        return <Navigate to="/admin" replace />;
    }

    return (
        <Box>
            <Breadcrumbs sx={{ mb: 2 }}>
                <Link component={RouterLink} underline="hover" color="inherit" to="/admin">
                    Dashboard
                </Link>
                <Link component={RouterLink} underline="hover" color="inherit" to="/admin/users">
                    Usuários
                </Link>
                <Typography color="text.primary">{isEdit ? 'Editar' : 'Novo'}</Typography>
            </Breadcrumbs>

            <Typography variant="h5" gutterBottom>
                {isEdit ? 'Editar usuário' : 'Cadastrar usuário'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Gestão restrita ao master: dados, plano e status da conta.
                {!isEdit && ' O usuário já nasce ativo e pode entrar sem confirmar e-mail.'}
            </Typography>

            <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 640 }}>
                {formData.is_master && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Esta é a conta master. Ela não pode ser inativada.
                    </Alert>
                )}

                <TextField
                    label="Nome"
                    fullWidth
                    required
                    margin="normal"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    error={Boolean(errors.name)}
                    helperText={errors.name}
                />
                <TextField
                    label="E-mail"
                    type="email"
                    fullWidth
                    required
                    margin="normal"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    error={Boolean(errors.email)}
                    helperText={errors.email}
                />
                <TextField
                    label={isEdit ? 'Nova senha (opcional)' : 'Senha'}
                    type="password"
                    fullWidth
                    required={!isEdit}
                    margin="normal"
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    error={Boolean(errors.password)}
                    helperText={errors.password || (isEdit ? 'Deixe em branco para manter a senha atual.' : 'Mínimo de 8 caracteres.')}
                />
                <TextField
                    label="Nome da loja"
                    fullWidth
                    margin="normal"
                    value={formData.store_name}
                    onChange={(e) => handleChange('store_name', e.target.value)}
                />
                <TextField
                    select
                    label="Plano"
                    fullWidth
                    margin="normal"
                    required
                    value={formData.subscription_uuid}
                    onChange={(e) => handleChange('subscription_uuid', e.target.value)}
                    helperText="Plano de assinatura do usuário."
                >
                    {plans.map((plan) => (
                        <MenuItem key={plan.uuid} value={plan.uuid}>
                            {plan.name}
                        </MenuItem>
                    ))}
                </TextField>

                <FormControlLabel
                    sx={{ mt: 1, display: 'block' }}
                    control={
                        <Switch
                            checked={formData.active}
                            disabled={Boolean(formData.is_master)}
                            onChange={(e) => handleChange('active', e.target.checked)}
                        />
                    }
                    label={formData.active ? 'Conta ativa' : 'Conta inativa'}
                />

                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                    <Button type="submit" variant="contained" disabled={loading}>
                        {loading ? <CircularProgress size={22} color="inherit" /> : 'Salvar'}
                    </Button>
                    <Button variant="outlined" onClick={() => navigate('/admin/users')} disabled={loading}>
                        Cancelar
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}

export function RegisterUserPage() {
    return <UserFormPage mode="create" />;
}

export function EditUserPage() {
    return <UserFormPage mode="edit" />;
}

export default RegisterUserPage;
